import React, { useState, useMemo } from 'react';

const STATUS_COLORS = {
  'To do': '#3b82f6', 'In Progress': '#f59e0b', 'Done': '#10b981',
  'Escalated': '#f43f5e', 'Backlog': '#94a3b8', 'Review': '#8b5cf6',
  'Open': '#3b82f6', 'Resolved': '#10b981',
};
const PRIORITY_COLORS = {
  'Critical': '#f43f5e', 'High': '#f97316', 'Medium': '#f59e0b', 'Low': '#22c55e',
};

function badgeColor(fieldName, value) {
  const lower = (fieldName || '').toLowerCase();
  if (lower === 'status')   return STATUS_COLORS[value]   || '#94a3b8';
  if (lower === 'priority') return PRIORITY_COLORS[value] || '#94a3b8';
  return '#64748b';
}

/**
 * FunctionTaskView — Human task queue UI for Function nodes.
 *
 * Props:
 *   node      – the Function node (with data.taskFields, data.tasks, data.agents, etc.)
 *   allNodes  – all pipeline nodes (used to resolve sourceRef collection names)
 *   onUpdate  – (nodeId, dataPatch) => void
 *   onClose   – () => void
 *   opsMode   – boolean; when true, relabel Tasks → Work Items, etc.
 */
export default function FunctionTaskView({ node, allNodes, onUpdate, onClose, opsMode }) {
  const taskFields = node.data.taskFields || [];
  const tasks      = useMemo(() => node.data.tasks  || [], [node.data.tasks]);
  const agents     = useMemo(() => node.data.agents || [], [node.data.agents]);

  // ── Labels ──
  const taskLabel       = opsMode ? 'Work Item'  : 'Task';
  const tasksLabel      = opsMode ? 'Work Items' : 'Tasks';
  const assigneeLabel   = opsMode ? 'Employee'   : 'Assignee';

  // ── Filters ──
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [searchText,     setSearchText]     = useState('');

  // ── New-task form ──
  const [adding,    setAdding]    = useState(false);
  const [newValues, setNewValues] = useState({});

  // ── Expanded row ──
  const [expandedId, setExpandedId] = useState(null);

  // ── Helpers to find special fields ──
  const statusField   = taskFields.find(f => f.name.toLowerCase() === 'status');
  const assigneeField = taskFields.find(f => f.type === 'agent');
  const titleField    = taskFields.find(f => f.type === 'text');

  // ── Unique status options (for filter dropdown) ──
  const statusOptions = useMemo(() => {
    if (statusField?.options) return statusField.options;
    const vals = new Set(tasks.map(t => t.values?.[statusField?.id]).filter(Boolean));
    return [...vals];
  }, [statusField, tasks]);

  // ── Unique assignees (for filter dropdown) ──
  const assigneeOptions = useMemo(() => {
    const names = agents.map(a => a.name);
    tasks.forEach(t => {
      const v = assigneeField ? t.values?.[assigneeField.id] : null;
      if (v && !names.includes(v)) names.push(v);
    });
    return names;
  }, [agents, tasks, assigneeField]);

  // ── Filtered tasks ──
  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (filterStatus && statusField) {
        if (t.values?.[statusField.id] !== filterStatus) return false;
      }
      if (filterAssignee && assigneeField) {
        if (t.values?.[assigneeField.id] !== filterAssignee) return false;
      }
      if (searchText.trim()) {
        const q = searchText.toLowerCase();
        const match = Object.values(t.values || {}).some(v =>
          String(v).toLowerCase().includes(q)
        );
        if (!match) return false;
      }
      return true;
    });
  }, [tasks, filterStatus, filterAssignee, searchText, statusField, assigneeField]);

  // ── Data helpers ──
  const saveTasks = (updated) => onUpdate(node.id, { tasks: updated });

  const addTask = () => {
    const t = {
      id: `t${Date.now()}`,
      values: { ...newValues },
      createdAt: new Date().toISOString(),
    };
    // Apply defaults from field definitions
    taskFields.forEach(f => {
      if (f.default && !t.values[f.id]) t.values[f.id] = f.default;
    });
    saveTasks([...tasks, t]);
    setNewValues({});
    setAdding(false);
  };

  const deleteTask = (tid) => saveTasks(tasks.filter(t => t.id !== tid));

  const updateTaskValue = (tid, fid, val) => {
    saveTasks(tasks.map(t =>
      t.id === tid ? { ...t, values: { ...t.values, [fid]: val } } : t
    ));
  };

  // ── Resolve source collection name ──
  const resolveSource = (sourceRef) => {
    if (!sourceRef) return null;
    const colNode = (allNodes || []).find(n => n.id === sourceRef.collectionId);
    const colName = colNode?.data?.label || sourceRef.collectionId;
    return { colName, recordId: sourceRef.recordId };
  };

  // ── Render a field value cell ──
  const renderFieldValue = (task, field, isExpanded = false) => {
    const val = task.values?.[field.id] || '';
    const isStatus   = field.name.toLowerCase() === 'status';
    const isPriority = field.name.toLowerCase() === 'priority';
    const isSelect   = field.type === 'select';
    const isAgent    = field.type === 'agent';

    if (isSelect || isAgent) {
      const color = (isStatus || isPriority) ? badgeColor(field.name, val) : null;
      return (
        <select
          className="ftv-inline-select"
          value={val}
          onChange={e => updateTaskValue(task.id, field.id, e.target.value)}
          style={color ? { color, borderColor: color + '44', background: color + '11' } : undefined}
        >
          <option value="">--</option>
          {isAgent
            ? agents.map(a => <option key={a.id} value={a.name}>{a.name}</option>)
            : (field.options || []).map(o => <option key={o} value={o}>{o}</option>)
          }
        </select>
      );
    }

    if (isExpanded) {
      return (
        <input
          className="ftv-inline-input"
          value={val}
          placeholder={field.name}
          onChange={e => updateTaskValue(task.id, field.id, e.target.value)}
        />
      );
    }

    return <span className="ftv-cell-text">{val || '--'}</span>;
  };

  // ── Render a badge for status / priority in the compact row ──
  const renderBadge = (task, field) => {
    const val = task.values?.[field.id];
    if (!val) return null;
    const color = badgeColor(field.name, val);
    return (
      <span
        className="col-badge"
        style={{ background: color + '22', color }}
      >
        {val}
      </span>
    );
  };

  // ── Choose visible columns (first 4 meaningful fields for compact row) ──
  const compactFields = taskFields.slice(0, 4);

  return (
    <div className="ftv-overlay">
      <div className="ftv-panel">

        {/* ── Header ── */}
        <div className="ftv-header">
          <div className="ftv-header-left">
            <span className="ftv-header-icon" style={{ background: 'var(--node-function)' }}>
              {opsMode ? 'W' : 'T'}
            </span>
            <div className="ftv-header-info">
              <h3 className="ftv-title">{node.data.label || 'Function'}</h3>
              <span className="ftv-subtitle">
                {filtered.length}{filtered.length !== tasks.length ? ` / ${tasks.length}` : ''} {tasksLabel.toLowerCase()}
              </span>
            </div>
          </div>
          <button className="ftv-close-btn" onClick={onClose}>x</button>
        </div>

        {/* ── Filters ── */}
        <div className="ftv-filters">
          <input
            className="ftv-search"
            placeholder={`Search ${tasksLabel.toLowerCase()}...`}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {statusField && statusOptions.length > 0 && (
            <select
              className="ftv-filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {assigneeField && assigneeOptions.length > 0 && (
            <select
              className="ftv-filter-select"
              value={filterAssignee}
              onChange={e => setFilterAssignee(e.target.value)}
            >
              <option value="">All {opsMode ? 'employees' : 'assignees'}</option>
              {assigneeOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          <button
            className="ftv-add-btn"
            onClick={() => setAdding(a => !a)}
          >
            {adding ? 'Cancel' : `+ New ${taskLabel}`}
          </button>
        </div>

        {/* ── New task form ── */}
        {adding && (
          <div className="ftv-add-form">
            <div className="ftv-add-form-title">New {taskLabel}</div>
            <div className="ftv-add-form-fields">
              {taskFields.map(f => (
                <div key={f.id} className="ftv-add-field">
                  <label className="panel-label">
                    {f.name}
                    {f.required && <span className="ftv-required">*</span>}
                  </label>
                  {f.type === 'select' ? (
                    <select
                      className="panel-input"
                      value={newValues[f.id] || f.default || ''}
                      onChange={e => setNewValues(p => ({ ...p, [f.id]: e.target.value }))}
                    >
                      <option value="">-- pick --</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === 'agent' ? (
                    <select
                      className="panel-input"
                      value={newValues[f.id] || ''}
                      onChange={e => setNewValues(p => ({ ...p, [f.id]: e.target.value }))}
                    >
                      <option value="">-- pick {assigneeLabel.toLowerCase()} --</option>
                      {agents.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                    </select>
                  ) : (
                    <input
                      className="panel-input"
                      placeholder={f.name}
                      value={newValues[f.id] || ''}
                      onChange={e => setNewValues(p => ({ ...p, [f.id]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
            <button className="ftv-submit-btn" onClick={addTask}>
              Create {taskLabel}
            </button>
          </div>
        )}

        {/* ── Task list / table ── */}
        <div className="ftv-task-list">
          {/* Column headers */}
          {taskFields.length > 0 && filtered.length > 0 && (
            <div className="ftv-table-header">
              {compactFields.map(f => (
                <div key={f.id} className={`ftv-th ${f === titleField ? 'ftv-th-title' : ''}`}>
                  {f.name}
                </div>
              ))}
              <div className="ftv-th ftv-th-actions">Actions</div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="panel-empty" style={{ padding: '24px 0', textAlign: 'center' }}>
              {tasks.length === 0
                ? `No ${tasksLabel.toLowerCase()} yet. Click "+ New ${taskLabel}" to create one.`
                : `No ${tasksLabel.toLowerCase()} match the current filters.`
              }
            </div>
          )}

          {filtered.map(task => {
            const isExpanded = expandedId === task.id;
            const source = resolveSource(task.sourceRef);

            return (
              <div key={task.id} className={`ftv-task-row${isExpanded ? ' expanded' : ''}`}>
                {/* Compact row */}
                <div
                  className="ftv-task-compact"
                  onClick={() => setExpandedId(isExpanded ? null : task.id)}
                >
                  <div className="ftv-task-cells">
                    {compactFields.map(f => (
                      <div key={f.id} className={`ftv-cell ${f === titleField ? 'ftv-cell-title' : ''}`} data-label={f.name}>
                        {(f.name.toLowerCase() === 'status' || f.name.toLowerCase() === 'priority')
                          ? renderBadge(task, f)
                          : f.type === 'agent'
                            ? <span className="ftv-cell-agent">{task.values?.[f.id] || '--'}</span>
                            : <span className="ftv-cell-text">{task.values?.[f.id] || '--'}</span>
                        }
                      </div>
                    ))}
                  </div>
                  <div className="ftv-task-row-actions">
                    {source && (
                      <span className="ftv-source-link" title={`From: ${source.colName} / ${source.recordId}`}>
                        Source
                      </span>
                    )}
                    <button
                      className="ftv-delete-btn"
                      title={`Delete ${taskLabel.toLowerCase()}`}
                      onClick={e => { e.stopPropagation(); deleteTask(task.id); }}
                    >
                      x
                    </button>
                    <span className="ftv-expand-chevron">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="ftv-task-detail">
                    {taskFields.map(f => (
                      <div key={f.id} className="ftv-detail-row">
                        <label className="ftv-detail-label">{f.name}</label>
                        <div className="ftv-detail-value">
                          {renderFieldValue(task, f, true)}
                        </div>
                      </div>
                    ))}
                    {source && (
                      <div className="ftv-detail-row">
                        <label className="ftv-detail-label">Source</label>
                        <div className="ftv-detail-value">
                          <span className="ftv-source-badge">
                            {source.colName} &rarr; {source.recordId}
                          </span>
                        </div>
                      </div>
                    )}
                    {task.createdAt && (
                      <div className="ftv-detail-row">
                        <label className="ftv-detail-label">Created</label>
                        <div className="ftv-detail-value">
                          <span className="ftv-cell-text">
                            {new Date(task.createdAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
